(() => {
  const OPEN_CLASS = 'is-open';
  const SCROLL_LOCK_CLASS = 'info-modal-scroll-lock';
  const MODAL_SELECTOR = '[data-info-modal]';

  const toHandle = (value) => {
    const str = String(value || '').trim().toLowerCase();
    // Rough Shopify handleize approximation for editor-entered text.
    return str
      .replace(/['"`]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const escapeCss = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/[^a-zA-Z0-9_\-]/g, (m) => `\\${m}`);
  };

  const getFocusable = (container) => {
    const selector = [
      'a[href]',
      'area[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    return Array.from(container.querySelectorAll(selector)).filter((el) => {
      if (!el) return false;
      if (el.hasAttribute('disabled')) return false;
      const style = window.getComputedStyle(el);
      return style.visibility !== 'hidden' && style.display !== 'none';
    });
  };

  const lockScroll = () => {
    const html = document.documentElement;
    const body = document.body;
    if (!html || !body) return;

    if (html.classList.contains(SCROLL_LOCK_CLASS)) return;

    const scrollBarWidth = window.innerWidth - html.clientWidth;
    body.dataset.infoModalPrevPaddingRight = body.style.paddingRight || '';
    if (scrollBarWidth > 0) body.style.paddingRight = `${scrollBarWidth}px`;

    html.classList.add(SCROLL_LOCK_CLASS);
  };

  const unlockScroll = () => {
    const html = document.documentElement;
    const body = document.body;
    if (!html || !body) return;

    html.classList.remove(SCROLL_LOCK_CLASS);
    if (body.dataset.infoModalPrevPaddingRight !== undefined) {
      body.style.paddingRight = body.dataset.infoModalPrevPaddingRight;
      delete body.dataset.infoModalPrevPaddingRight;
    }
  };

  let activeModal = null;
  let openerEl = null;
  let onKeydown = null;
  let onOverlayClick = null;

  const closeModal = (modal) => {
    if (!modal) return;

    modal.setAttribute('aria-hidden', 'true');
    modal.classList.remove(OPEN_CLASS);

    const finish = () => {
      modal.hidden = true;
      modal.removeEventListener('transitionend', finish);
    };

    modal.addEventListener('transitionend', finish);
    // Fallback in case transitionend doesn't fire
    window.setTimeout(() => {
      if (!modal.classList.contains(OPEN_CLASS)) modal.hidden = true;
    }, 250);

    if (onKeydown) {
      document.removeEventListener('keydown', onKeydown);
      onKeydown = null;
    }

    if (onOverlayClick) {
      modal.removeEventListener('click', onOverlayClick);
      onOverlayClick = null;
    }

    unlockScroll();

    const toFocus = openerEl;
    activeModal = null;
    openerEl = null;

    if (toFocus && document.contains(toFocus)) {
      window.setTimeout(() => toFocus.focus(), 0);
    }
  };

  const openModal = (modal, opener) => {
    if (!modal) return;
    if (activeModal && activeModal !== modal) closeModal(activeModal);

    openerEl = opener || null;
    activeModal = modal;

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add(OPEN_CLASS);
    lockScroll();

    const dialog = modal.querySelector('.info-modal__dialog') || modal;
    const closeBtn = modal.querySelector('.info-modal__close');
    const focusTarget = closeBtn || getFocusable(dialog)[0] || dialog;

    window.setTimeout(() => {
      try {
        focusTarget.focus({ preventScroll: true });
      } catch (e) {
        focusTarget.focus();
      }
    }, 0);

    onKeydown = (evt) => {
      if (!activeModal || activeModal !== modal) return;

      if (evt.key === 'Escape') {
        evt.preventDefault();
        closeModal(modal);
        return;
      }

      if (evt.key !== 'Tab') return;

      const focusables = getFocusable(dialog);
      if (!focusables.length) {
        evt.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (!dialog.contains(active)) {
        evt.preventDefault();
        (evt.shiftKey ? last : first).focus();
        return;
      }

      if (evt.shiftKey) {
        if (active === first) {
          evt.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          evt.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeydown);

    onOverlayClick = (e) => {
      if (!activeModal || activeModal !== modal) return;
      if (!dialog.contains(e.target)) {
        e.preventDefault();
        closeModal(modal);
      }
    };
    modal.addEventListener('click', onOverlayClick);
  };

  const getModalById = (id) => {
    if (!id) return null;
    const sel = `[id="${escapeCss(id)}"]${MODAL_SELECTOR}`;
    try {
      return document.querySelector(sel);
    } catch (e) {
      const byId = document.getElementById(id);
      if (byId && byId.matches && byId.matches(MODAL_SELECTOR)) return byId;
      return null;
    }
  };

  const getHashFromLink = (anchor) => {
    // 1. Check the raw attribute — works for href="#modal:id"
    const raw = anchor.getAttribute('href') || '';
    if (raw.startsWith('#') && raw !== '#') return raw.slice(1);
    // 2. Check the resolved .hash — works for full URLs with fragments
    //    e.g. https://eplehuset.no/#modal:testing-modal
    const resolved = anchor.hash || '';
    if (resolved.length > 1) return resolved.slice(1);
    return '';
  };

  const resolveModalFromHash = (raw) => {
    if (!raw) return null;
    const decoded = (() => {
      try { return decodeURIComponent(raw); } catch (e) { return raw; }
    })();
    const idFromPrefix = decoded.startsWith('modal:') ? decoded.slice('modal:'.length) : '';
    const targetId = (idFromPrefix || decoded).trim();
    if (!targetId) return null;
    const candidates = [targetId];
    const handled = toHandle(targetId);
    if (handled && handled !== targetId) candidates.push(handled);
    return candidates.map(getModalById).find(Boolean) || null;
  };

  const onClick = (evt) => {
    // 1. Close trigger (cheapest — skip entirely when no modal is open)
    if (activeModal) {
      const closeTrigger = evt.target.closest('[data-modal-close]');
      if (closeTrigger) {
        const modal = closeTrigger.closest(MODAL_SELECTOR);
        if (modal && modal === activeModal) {
          evt.preventDefault();
          closeModal(modal);
          return;
        }
      }
    }

    // 2. Explicit data-attribute trigger (simple presence check)
    const openTrigger = evt.target.closest('[data-modal-target]');
    if (openTrigger) {
      const targetId = openTrigger.getAttribute('data-modal-target');
      const modal = getModalById(targetId) || getModalById(toHandle(targetId));
      if (!modal) return;

      evt.preventDefault();
      openModal(modal, openTrigger);
      return;
    }

    // 3. Anchor link whose hash matches a modal id:
    //    href="#modal:your-id"  |  href="#your-id"
    //    href="/page#modal:your-id"  |  full-url#modal:your-id
    const anchor = evt.target.closest('a[href]');
    if (anchor) {
      const rawHash = getHashFromLink(anchor);
      if (rawHash) {
        const modal = resolveModalFromHash(rawHash);
        if (modal) {
          evt.preventDefault();
          openModal(modal, anchor);
          return;
        }
      }
    }

    // 4. Intercept inline onclick="location.href='#modal:...'" on ancestor elements
    //    (expensive substring selector — checked last, hashchange listener is the fallback)
    const onclickEl = evt.target.closest('[onclick*="modal:"]');
    if (onclickEl) {
      const attr = onclickEl.getAttribute('onclick') || '';
      const match = attr.match(/#modal:([^'"\s;)]+)/);
      if (match) {
        const modal = resolveModalFromHash('modal:' + match[1]);
        if (modal) {
          evt.preventDefault();
          evt.stopPropagation();
          openModal(modal, onclickEl);
          return;
        }
      }
    }
  };

  const onSectionUnload = (evt) => {
    if (!activeModal) return;
    const sectionId = evt && evt.detail && evt.detail.sectionId;
    if (!sectionId) return;
    const modalSectionId = activeModal.getAttribute('data-section-id');
    if (modalSectionId && modalSectionId === sectionId) closeModal(activeModal);
  };

  /* ── Theme Editor integration ──────────────────────── */

  const getModalForEditorEvent = (evt) => {
    const sectionId = evt && evt.detail && evt.detail.sectionId;
    if (!sectionId) return null;
    return document.querySelector(`${MODAL_SELECTOR}[data-section-id="${escapeCss(sectionId)}"]`);
  };

  const onSectionSelect = (evt) => {
    const modal = getModalForEditorEvent(evt);
    if (modal) openModal(modal);
  };

  const onSectionDeselect = (evt) => {
    const modal = getModalForEditorEvent(evt);
    if (modal && activeModal === modal) closeModal(modal);
  };

  const onBlockSelect = (evt) => {
    const target = evt && evt.target;
    if (!target) return;
    const modal = target.closest(MODAL_SELECTOR);
    if (modal && activeModal !== modal) openModal(modal);
  };

  const onBlockDeselect = (evt) => {
    const target = evt && evt.target;
    if (!target) return;
    const modal = target.closest(MODAL_SELECTOR);
    if (modal && activeModal === modal) closeModal(modal);
  };

  // Handle location.href = '#modal:id' (used by onclick buttons)
  const onHashChange = () => {
    const hash = window.location.hash;
    if (!hash || hash === '#') return;

    const raw = hash.slice(1);
    const decoded = (() => {
      try { return decodeURIComponent(raw); } catch (e) { return raw; }
    })();

    const idFromModalPrefix = decoded.startsWith('modal:') ? decoded.slice('modal:'.length) : '';
    const targetId = (idFromModalPrefix || decoded).trim();
    if (!targetId) return;

    const modal = getModalById(targetId) || getModalById(toHandle(targetId));
    if (modal) {
      // Clear the hash so the same modal can be re-triggered
      history.replaceState(null, '', window.location.pathname + window.location.search);
      openModal(modal);
    }
  };

  // Capture phase so other scripts can't swallow the click.
  document.addEventListener('click', onClick, true);
  window.addEventListener('hashchange', onHashChange);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:section:select', onSectionSelect);
  document.addEventListener('shopify:section:deselect', onSectionDeselect);
  document.addEventListener('shopify:block:select', onBlockSelect);
  document.addEventListener('shopify:block:deselect', onBlockDeselect);
})();